/**
 * Where a default save puts the look of a gallery background: its brightness
 * and its opacity (libs/bgLook.ts).
 *
 * The look belongs to the background, as its crop and its banner images do
 * (cropRowTarget.ts). A save at the set level wrote the look of the background
 * on screen to the row of the set. Every background whose own row has no look
 * then took that look: an admin made one background lighter, and all of them
 * changed.
 *
 * So a save at the set or the layout level writes the look to the row of the
 * background on screen, and only the fields the admin changed. An unchanged
 * look is not written: in the row of the set it would also give the look of
 * this background to the other backgrounds. A save at a background level
 * writes a row of the background already. A save at a variant level writes
 * the row of one treatment, which the admin chose, and is left as it was.
 *
 * The colours of the banners on a background follow the same rule: the base
 * colour, the text colour and the logos (BG_COLOUR_FIELDS). A save at the
 * product level on the light background 38 of PyCharm Social wrote its base,
 * its black text and its black logos to the row of the product (2026-10-27).
 * Every old dark background has no row of its own, so all of them took black
 * text on a dark image.
 */
import {BG_LOOK_FIELDS, defaultBgLook, type BgLook, type BgLookField} from "libs/bgLook";
import {cascadeKeys, rowKey, type OverrideRow, type RowSpecificity} from "./cropRowTarget";

/** The colours of the banners that belong to the background on screen. */
export const BG_COLOUR_FIELDS = ["base", "bases", "textColorLocked", "logoVariants"] as const;

export type BgColourField = (typeof BG_COLOUR_FIELDS)[number];

/** The colour fields a save writes, with the values on screen. */
export type BgColours = Partial<Record<BgColourField, unknown>>;

/** Every field that goes to the row of the background: the look and the colours. */
export const LOOK_ROW_FIELDS: readonly (BgLookField | BgColourField)[] = Object.freeze([
  ...BG_LOOK_FIELDS,
  ...BG_COLOUR_FIELDS,
]);

export type LookRowField = (typeof LOOK_ROW_FIELDS)[number];

export interface LookRowTargetInput {
  /** Every row of the channel, as the panel fetches them. */
  overrides: OverrideRow[] | null | undefined;
  /** The route the editor is on. */
  channel: string;
  product: string;
  type: string;
  set: string | null | undefined;
  layout: string | null | undefined;
  background: string;
  variant?: string | null;
  /** The look on screen. */
  look: BgLook;
  /** The colour fields the save writes, as on screen. A field not here is not saved. */
  colours?: BgColours;
  /** The row the save writes the crop and the banner images to (backgroundSlotTarget). */
  slot: RowSpecificity | null;
  /** The row the admin's Level writes. */
  chosenLayout: string | null | undefined;
  chosenBackground: string | null | undefined;
  chosenVariant: string | null | undefined;
}

export interface LookRowTarget {
  /** The look and colour fields the save writes. It leaves out the others. */
  fields: LookRowField[];
  /** The row they go to, or null for the chosen row. */
  row: RowSpecificity | null;
}

type RouteInput = Omit<
  LookRowTargetInput,
  "look" | "colours" | "slot" | "chosenLayout" | "chosenBackground" | "chosenVariant"
>;

/** The scene data of the route's rows, least specific first. */
function routeRows({overrides, channel, product, type, set, layout, background, variant}: RouteInput) {
  const keys = cascadeKeys(product, layout, background, variant);
  const rank = new Map(keys.map((key, index) => [key, index]));
  return (overrides ?? [])
    .filter(
      (row) =>
        row.channel === channel &&
        row.type === type &&
        (row.set || "") === (set || "") &&
        row.scene_data &&
        rank.has(rowKey(row))
    )
    .sort((a, b) => rank.get(rowKey(a))! - rank.get(rowKey(b))!)
    .map((row) => row.scene_data!);
}

/** The look the rows give the route: the most specific row with a field wins. */
export function resolvedRowLook(input: RouteInput): BgLook {
  const merged: Record<string, unknown> = {};
  for (const scene of routeRows(input)) {
    for (const field of BG_LOOK_FIELDS) {
      if (scene[field] !== undefined) merged[field] = scene[field];
    }
  }
  return defaultBgLook(merged);
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/**
 * The colours the rows give the route. The base: the most specific row wins.
 * The per-banner records: per banner, as the cascade merges them.
 */
export function resolvedRowColours(input: RouteInput): BgColours {
  const merged: Record<string, unknown> = {};
  for (const scene of routeRows(input)) {
    for (const field of BG_COLOUR_FIELDS) {
      const value = scene[field];
      if (value === undefined) continue;
      merged[field] =
        isRecord(value) && isRecord(merged[field])
          ? {...(merged[field] as Record<string, unknown>), ...value}
          : value;
    }
  }
  return merged;
}

/** A hex colour without "#", in lower case. */
const hexKey = (value: unknown) => String(value).replace(/^#/, "").toLowerCase();

/**
 * One text for equal colours. An empty value is no colour: a banner with no
 * locked text colour, no logo variant or no base of its own follows the rest.
 */
function colourKey(field: BgColourField, value: unknown): string {
  const empty = (v: unknown) => v === undefined || v === null || v === "";
  if (field === "base") return empty(value) ? "" : hexKey(value);
  if (!isRecord(value)) return "[]";
  const entries = Object.entries(value)
    .filter(([, v]) => !empty(v))
    .map(([id, v]) => [id, field === "bases" ? hexKey(v) : v] as const)
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

/**
 * The look and colour fields a save writes, and their row. Null when the save
 * writes them to the chosen row as before: a background level, a variant level.
 */
export function lookRowTarget(input: LookRowTargetInput): LookRowTarget | null {
  const {look, colours = {}, slot, chosenLayout, chosenBackground, chosenVariant, background} =
    input;
  if (chosenBackground || chosenVariant) return null;

  const resolved = resolvedRowLook(input);
  const resolvedColours = resolvedRowColours(input);
  const fields: LookRowField[] = [
    ...BG_LOOK_FIELDS.filter((field) => look[field] !== resolved[field]),
    ...BG_COLOUR_FIELDS.filter(
      (field) =>
        field in colours &&
        colourKey(field, colours[field]) !== colourKey(field, resolvedColours[field])
    ),
  ];
  if (fields.length === 0) return {fields, row: null};

  // A crop that goes to a row of this background takes the look with it:
  // that row outranks the background row, so the look shows. A crop that goes
  // to a row of the whole layout leaves the look on the chosen row.
  if (slot) return {fields, row: slot.background ? slot : null};
  return {fields, row: {layout: chosenLayout || null, background, variant: null}};
}
